/**
 * Round Robin Component
 *
 * 모든 선수가 한 번씩 대진하는 Round Robin 토너먼트 컴포넌트
 * - 승자 선택 시 자동으로 승/패 업데이트
 * - 워닝/파울은 자동으로 0으로 초기화
 * - 승점 계산 및 순위 표시
 */

import React, { useMemo } from 'react';
import '@assets/css/comp-form-trmntBracket.css';
import '@assets/css/competition.css';

const CompRoundRobin = ({
    players = [],
    matches = [],
    theme = 'light',
    onWinnerSelect,
    isFromHome = false
}) => {
    /**
     * 승점 계산 및 순위 생성
     * 승점 = 승 * 3
     * 동점일 경우 승수가 많은 선수가 상위
     */
    const standings = useMemo(() => {
        // 각 선수별 통계 집계
        const playerStats = {};

        players.forEach(player => {
            if (!player || !player.name) return;

            playerStats[player.name] = {
                name: player.name,
                wins: 0,
                losses: 0,
                draws: 0,
                points: 0
            };
        });

        // 매치 결과를 기반으로 통계 업데이트
        matches.forEach(match => {
            if (!match.playerA || !match.playerB) return;

            if (match.winner === 'A') {
                if (playerStats[match.playerA]) {
                    playerStats[match.playerA].wins += 1;
                    playerStats[match.playerA].points += 3;
                }
                if (playerStats[match.playerB]) {
                    playerStats[match.playerB].losses += 1;
                }
            } else if (match.winner === 'B') {
                if (playerStats[match.playerB]) {
                    playerStats[match.playerB].wins += 1;
                    playerStats[match.playerB].points += 3;
                }
                if (playerStats[match.playerA]) {
                    playerStats[match.playerA].losses += 1;
                }
            }
        });

        // 순위 정렬 (승점 > 승수 > 이름)
        const sortedStats = Object.values(playerStats).sort((a, b) => {
            if (b.points !== a.points) return b.points - a.points;
            if (b.wins !== a.wins) return b.wins - a.wins;
            return a.name.localeCompare(b.name);
        });

        return sortedStats;
    }, [players, matches]);

    /**
     * 승자 선택 핸들러
     */
    const handleWinnerClick = (matchIndex, player) => {
        if (isFromHome) return;
        onWinnerSelect(matchIndex, player);
    };

    /**
     * 순위표 렌더링
     */
    const renderStandings = () => {
        if (standings.length === 0) return null;

        return (
            <div className="card shadow mb-4" style={{
                backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid rgba(0, 0, 0, 0.2)',
            }}>
                <div className="card-header" style={{
                    backgroundColor: theme === 'dark' ? '#4a5568' : '#198754',
                    color: '#ffffff',
                    borderBottom: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.4)' : '2px solid rgba(0, 0, 0, 0.2)',
                    padding: '1rem 1.25rem',
                    fontWeight: 'bold'
                }}>
                    <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                        <i className="bi bi-trophy-fill me-2" style={{ color: '#ffd700' }}></i>
                        순위표
                    </h5>
                </div>
                <div className="card-body p-3">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0" style={{
                            color: theme === 'dark' ? '#e0e0e0' : '#000'
                        }}>
                            <thead style={{
                                backgroundColor: theme === 'dark' ? '#1a1a2e' : '#f8f9fa',
                                borderBottom: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid #dee2e6'
                            }}>
                                <tr>
                                    <th className="text-center" style={{ width: '60px' }}>순위</th>
                                    <th>선수명</th>
                                    <th className="text-center" style={{ width: '80px' }}>승점</th>
                                    <th className="text-center" style={{ width: '60px' }}>승</th>
                                    <th className="text-center" style={{ width: '60px' }}>패</th>
                                </tr>
                            </thead>
                            <tbody>
                                {standings.map((stat, index) => {
                                    // 순위 배지 색상 - 4등 이상은 dark 색상으로 잘 보이게 변경
                                    const rankBadgeColor = index === 0 ? 'warning' : index === 1 ? 'secondary' : index === 2 ? 'info' : 'dark';
                                    const rankIcon = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '';

                                    return (
                                        <tr key={`standing-${index}`} style={{
                                            backgroundColor: index === 0
                                                ? (theme === 'dark' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 193, 7, 0.1)')
                                                : 'transparent'
                                        }}>
                                            <td className="text-center">
                                                <span className={`badge bg-${rankBadgeColor}`} style={{
                                                    fontSize: '0.85rem',
                                                    padding: '0.4rem 0.6rem',
                                                    minWidth: '40px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {rankIcon} {index + 1}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: index < 3 ? 'bold' : 'normal' }}>
                                                {stat.name}
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-primary" style={{
                                                    fontSize: '0.85rem',
                                                    padding: '0.4rem 0.6rem',
                                                    minWidth: '40px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {stat.points}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-success" style={{
                                                    fontSize: '0.85rem',
                                                    padding: '0.4rem 0.6rem',
                                                    minWidth: '35px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {stat.wins}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span className="badge bg-danger" style={{
                                                    fontSize: '0.85rem',
                                                    padding: '0.4rem 0.6rem',
                                                    minWidth: '35px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {stat.losses}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * 매치 카드 렌더링
     */
    const renderMatchCard = (match, matchIndex, isLast = false, roundMatchNumber = null) => {
        return (
            <div
                key={`match-${matchIndex}`}
                className="card shadow"
                style={{
                    backgroundColor: theme === 'dark' ? '#2d2d44' : '#ffffff',
                    border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid rgba(0, 0, 0, 0.2)',
                    marginBottom: isLast ? '0' : '1rem'
                }}
            >
                <div className="card-header" style={{
                    background: theme === 'dark'
                        ? 'linear-gradient(135deg, #5a6c7d 0%, #4a5c6d 100%)'
                        : 'linear-gradient(135deg, #a5b4fc 0%, #818cf8 100%)',
                    color: theme === 'dark' ? '#ffffff' : '#1e293b',
                    borderBottom: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid rgba(99, 102, 241, 0.3)',
                    padding: '0.75rem 1.25rem',
                    fontWeight: 'bold'
                }}>
                    <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        <i className="bi bi-trophy-fill me-2" style={{ color: theme === 'dark' ? '#ffd700' : '#6366f1' }}></i>
                        매치 {roundMatchNumber !== null ? `#${roundMatchNumber}` : `#${matchIndex + 1}`}
                    </h6>
                </div>
                <div className="card-body p-2 p-md-3">
                    <div className="row g-2 align-items-center">
                        {/* 선수 A */}
                        <div className="col-12 col-md-5">
                            <div className="p-2 p-md-3 rounded" style={{
                                backgroundColor: match.winner === 'A'
                                    ? (theme === 'dark' ? 'rgba(255, 215, 0, 0.15)' : '#fff9e6')
                                    : (theme === 'dark' ? '#16213e' : '#e8f4f8'),
                                border: match.winner === 'A'
                                    ? (theme === 'dark' ? '2px solid #ffd700' : '2px solid #ffc107')
                                    : (theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid #b8daff'),
                                boxShadow: match.winner === 'A' ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none',
                                transition: 'all 0.3s ease'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0" style={{
                                        color: theme === 'dark' ? '#e0e0e0' : '#000',
                                        fontSize: '1.1rem'
                                    }}>
                                        <i className="bi bi-person-fill me-2 text-primary"></i>
                                        {match.playerA || '선수 A'}
                                    </h6>
                                    <button
                                        className="btn btn-sm winner-btn"
                                        onClick={(e) => {
                                            const target = e.currentTarget;
                                            handleWinnerClick(matchIndex, 'A');
                                            setTimeout(() => {
                                                target.blur();
                                            }, 10);
                                        }}
                                        disabled={isFromHome}
                                        style={{
                                            width: '128px',
                                            fontSize: '0.65rem',
                                            padding: '0.4rem 0.5rem',
                                            fontWeight: 'bold',
                                            transition: 'none',
                                            backgroundColor: match.winner === 'A' ? '#ffc107' : 'transparent',
                                            color: match.winner === 'A' ? '#000' : '#ffc107',
                                            borderColor: '#ffc107',
                                            border: '1px solid #ffc107',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {match.winner === 'A' ? '🏆 WINNER' : 'WINNER'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* VS 중앙 배치 */}
                        <div className="col-12 col-md-2 text-center py-2">
                            <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '40px' }}>
                                <span
                                    className="badge bg-primary"
                                    style={{
                                        fontSize: '1.1rem',
                                        padding: '0.6rem 1.2rem',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    VS
                                </span>
                            </div>
                        </div>

                        {/* 선수 B */}
                        <div className="col-12 col-md-5">
                            <div className="p-2 p-md-3 rounded" style={{
                                backgroundColor: match.winner === 'B'
                                    ? (theme === 'dark' ? 'rgba(255, 215, 0, 0.15)' : '#fff9e6')
                                    : (theme === 'dark' ? '#16213e' : '#ffe8f0'),
                                border: match.winner === 'B'
                                    ? (theme === 'dark' ? '2px solid #ffd700' : '2px solid #ffc107')
                                    : (theme === 'dark' ? '1px solid rgba(255, 105, 180, 0.3)' : '1px solid #ffb8d4'),
                                boxShadow: match.winner === 'B' ? '0 0 10px rgba(255, 215, 0, 0.3)' : 'none',
                                transition: 'all 0.3s ease'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0" style={{
                                        color: theme === 'dark' ? '#e0e0e0' : '#000',
                                        fontSize: '1.1rem'
                                    }}>
                                        <i className="bi bi-person-fill me-2 text-danger"></i>
                                        {match.playerB || '선수 B'}
                                    </h6>
                                    <button
                                        className="btn btn-sm winner-btn"
                                        onClick={(e) => {
                                            const target = e.currentTarget;
                                            handleWinnerClick(matchIndex, 'B');
                                            setTimeout(() => {
                                                target.blur();
                                            }, 10);
                                        }}
                                        disabled={isFromHome}
                                        style={{
                                            width: '128px',
                                            fontSize: '0.65rem',
                                            padding: '0.4rem 0.5rem',
                                            fontWeight: 'bold',
                                            transition: 'none',
                                            backgroundColor: match.winner === 'B' ? '#ffc107' : 'transparent',
                                            color: match.winner === 'B' ? '#000' : '#ffc107',
                                            borderColor: '#ffc107',
                                            border: '1px solid #ffc107',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {match.winner === 'B' ? '🏆 WINNER' : 'WINNER'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`round-robin-wrapper ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
            <style>
                {`
                    .winner-btn {
                        -webkit-tap-highlight-color: transparent !important;
                        -webkit-touch-callout: none !important;
                        -webkit-user-select: none !important;
                        user-select: none !important;
                    }
                    .winner-btn:active,
                    .winner-btn:focus,
                    .winner-btn:focus-visible,
                    .winner-btn:focus-within,
                    .winner-btn:hover {
                        outline: none !important;
                        box-shadow: none !important;
                    }
                `}
            </style>

            <div className="section-header rounded mb-4 shadow-sm" style={{
                background: theme === 'dark'
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.25rem',
                border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
            }}>
                <h4 className="mb-0 text-white" style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                    <i className="bi bi-people-fill me-2"></i>
                    Round Robin 토너먼트
                </h4>
            </div>

            {/* 순위표 */}
            {renderStandings()}

            {/* 매치 목록 - 라운드별로 그룹화 */}
            <div className="matches-container">
                {(() => {
                    // 라운드별로 매치를 그룹화
                    const matchesByRound = {};
                    matches.forEach(match => {
                        const round = match.round || 1;
                        if (!matchesByRound[round]) {
                            matchesByRound[round] = [];
                        }
                        matchesByRound[round].push(match);
                    });

                    // 라운드 순서대로 렌더링
                    return Object.keys(matchesByRound).sort((a, b) => Number(a) - Number(b)).map(round => (
                        <div key={`round-${round}`} className="mb-5 round-container" style={{
                            backgroundColor: theme === 'dark' ? 'rgba(45, 45, 68, 0.5)' : 'rgba(240, 243, 255, 0.6)',
                            border: theme === 'dark' ? '3px solid rgba(255, 215, 0, 0.4)' : '3px solid #c7d2fe',
                            borderRadius: '12px',
                            padding: '1.5rem',
                            boxShadow: theme === 'dark'
                                ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                                : '0 4px 12px rgba(99, 102, 241, 0.15)'
                        }}>
                            {/* 라운드 헤더 */}
                            <div className="round-header mb-4 p-3 rounded" style={{
                                background: theme === 'dark'
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: theme === 'dark' ? '2px solid rgba(255, 215, 0, 0.5)' : '2px solid rgba(255, 255, 255, 0.5)',
                                boxShadow: '0 3px 8px rgba(0,0,0,0.2)'
                            }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0 text-white" style={{
                                        fontWeight: 'bold',
                                        fontSize: '1.4rem'
                                    }}>
                                        <i className="bi bi-calendar-event me-2"></i>
                                        라운드 {round}
                                    </h5>
                                    <span className="badge" style={{
                                        fontSize: '0.9rem',
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        color: '#fff',
                                        padding: '0.5rem 1rem'
                                    }}>
                                        {matchesByRound[round].length}개 매치
                                    </span>
                                </div>
                            </div>

                            {/* 라운드 내 매치들 */}
                            <div style={{
                                backgroundColor: theme === 'dark' ? 'rgba(26, 26, 46, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '8px',
                                padding: '1rem'
                            }}>
                                {matchesByRound[round].map((match, index) => {
                                    const isLast = index === matchesByRound[round].length - 1;
                                    return (
                                        <div key={`match-${match.matchId}`}>
                                            {renderMatchCard(match, matches.indexOf(match), isLast, index + 1)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ));
                })()}
            </div>

            {!isFromHome && (
                <div className="alert alert-info mt-3" style={{ fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>공평한 경기 진행:</strong> 각 라운드마다 모든 참가자가 경기를 진행합니다. 한 참가자가 연속으로 경기하지 않도록 스케줄링되어 있습니다.
                    승자 버튼을 클릭하여 승자를 선택하세요. 승점은 자동으로 계산됩니다.
                </div>
            )}
        </div>
    );
};

export default CompRoundRobin;
